// Optimized mock video data with stable references
export interface MockVideo {
  id: string;
  videoUrl: string;
  thumbnail: string;
  title: string;
  duration: number;
  hasAudio: boolean;
  originalPrompt: string;
  aspectRatio: string;
  isLiked?: boolean;
  likeCount?: number;
}

// Static mock data - created once and never recreated
const MOCK_VIDEOS: readonly MockVideo[] = Object.freeze([
  {
    id: 'video-1',
    videoUrl: '/placeholder-video.mp4',
    thumbnail: '/images/placeholders/1c7a1868-146c-47ac-ba0a-692e91f995ba.jpg',
    title: 'Kanye the Bear',
    duration: 15,
    hasAudio: true,
    originalPrompt: 'A bear ranting about video generation technology while walking through a forest',
    aspectRatio: '16:9',
    isLiked: false,
    likeCount: 42
  },
  {
    id: 'video-2',
    videoUrl: '/placeholder-video.mp4',
    thumbnail: '/images/placeholders/7ba7f059-3df3-4997-a3f8-390c7af75df8.jpg',
    title: 'Cat Running in Garden',
    duration: 12,
    hasAudio: false,
    originalPrompt: 'A playful cat running through a beautiful garden with flowers',
    aspectRatio: '16:9',
    isLiked: true,
    likeCount: 89
  },
  {
    id: 'video-3',
    videoUrl: '/placeholder-video.mp4',
    thumbnail: '/images/placeholders/9c420284-b0a9-42a1-b21a-4da3a1f2a2f5.jpg',
    title: 'Ocean Waves at Sunset',
    duration: 20,
    hasAudio: true,
    originalPrompt: 'Peaceful ocean waves crashing against rocks during golden hour',
    aspectRatio: '16:9',
    isLiked: false,
    likeCount: 156
  },
  {
    id: 'video-4',
    videoUrl: '/placeholder-video.mp4',
    thumbnail: '/images/placeholders/89e11d20-4534-4e08-b824-9249fd5c4ff8.jpg',
    title: 'City Street at Night',
    duration: 18,
    hasAudio: true,
    originalPrompt: 'Busy city street with neon lights and traffic at night',
    aspectRatio: '16:9',
    isLiked: true,
    likeCount: 234
  },
  {
    id: 'video-5',
    videoUrl: '/placeholder-video.mp4',
    thumbnail: '/images/placeholders/162a2395-99e2-472f-8d05-8f482b760aa4.jpg',
    title: 'Mountain Landscape',
    duration: 25,
    hasAudio: false,
    originalPrompt: 'Majestic mountain range with snow-capped peaks and blue sky',
    aspectRatio: '16:9',
    isLiked: false,
    likeCount: 78
  },
  {
    id: 'video-6',
    videoUrl: '/placeholder-video.mp4',
    thumbnail: '/images/placeholders/0361d323-503f-43c2-9db1-e9919cd5811c.jpg',
    title: 'Forest Path',
    duration: 22,
    hasAudio: true,
    originalPrompt: 'Serene forest path with sunlight filtering through trees',
    aspectRatio: '16:9',
    isLiked: true,
    likeCount: 167
  },
  {
    id: 'video-7',
    videoUrl: '/placeholder-video.mp4',
    thumbnail: '/images/placeholders/3431a967-f9cd-4c7b-8f44-2e907fd141bf.jpg',
    title: 'Abstract Geometric Shapes',
    duration: 16,
    hasAudio: false,
    originalPrompt: 'Colorful geometric shapes morphing and rotating in space',
    aspectRatio: '16:9',
    isLiked: false,
    likeCount: 45
  },
  {
    id: 'video-8',
    videoUrl: '/placeholder-video.mp4',
    thumbnail: '/images/placeholders/4883b972-6c28-4589-992a-fea3872c6fba.jpg',
    title: 'Butterfly in Garden',
    duration: 14,
    hasAudio: true,
    originalPrompt: 'Beautiful butterfly landing on colorful flowers in a garden',
    aspectRatio: '16:9',
    isLiked: true,
    likeCount: 203
  },
  {
    id: 'video-9',
    videoUrl: '/placeholder-video.mp4',
    thumbnail: '/images/placeholders/17432d4d-3f02-40f8-b618-fb956e8c066f.jpg',
    title: 'Waterfall Cascade',
    duration: 19,
    hasAudio: true,
    originalPrompt: 'Powerful waterfall cascading down rocky cliffs',
    aspectRatio: '16:9',
    isLiked: false,
    likeCount: 134
  },
  {
    id: 'video-10',
    videoUrl: '/placeholder-video.mp4',
    thumbnail: '/images/placeholders/449026cb-6731-491f-8c6a-ed577fc7eefd.jpg',
    title: 'Desert Dunes',
    duration: 17,
    hasAudio: false,
    originalPrompt: 'Golden sand dunes shifting in the desert wind',
    aspectRatio: '16:9',
    isLiked: true,
    likeCount: 92
  },
  {
    id: 'video-11',
    videoUrl: '/placeholder-video.mp4',
    thumbnail: '/images/placeholders/b14682b6-cd7c-4b73-b18c-95c92c27b64b.jpg',
    title: 'Fireplace Ambiance',
    duration: 30,
    hasAudio: true,
    originalPrompt: 'Cozy fireplace with crackling flames and warm lighting',
    aspectRatio: '16:9',
    isLiked: true,
    likeCount: 278
  },
  {
    id: 'video-12',
    videoUrl: '/placeholder-video.mp4',
    thumbnail: '/images/placeholders/c5bfe557-1d58-4df0-9ace-18f6771c231d.jpg',
    title: 'Space Nebula',
    duration: 24,
    hasAudio: false,
    originalPrompt: 'Colorful nebula with swirling cosmic dust and stars',
    aspectRatio: '16:9',
    isLiked: false,
    likeCount: 156
  }
] as const);

// Memoized getters with stable references
export const getMockVideos = (): readonly MockVideo[] => MOCK_VIDEOS;

export const getMockVideoById = (id: string): MockVideo | undefined => {
  return MOCK_VIDEOS.find(video => video.id === id);
};

// Memoized filter functions with stable references
export const getVideosByAudio = (hasAudio: boolean): readonly MockVideo[] => {
  return MOCK_VIDEOS.filter(video => video.hasAudio === hasAudio);
};

export const getFavoriteVideos = (): readonly MockVideo[] => {
  return MOCK_VIDEOS.filter(video => video.isLiked);
};

// Count helpers for stable references
export const getVideoCount = (): number => MOCK_VIDEOS.length;
export const getAudioVideoCount = (): number => MOCK_VIDEOS.filter(v => v.hasAudio).length;
export const getFavoriteVideoCount = (): number => MOCK_VIDEOS.filter(v => v.isLiked).length; 